/**
 * changelog.js — single source for the in-app changelog (Admin Panel ▸ Changelog).
 *
 * This is the human-readable release log: WHAT shipped, WHEN, and how big a deal
 * it was. Implementation detail stays in commit messages / feature specs.
 *
 * Versioning: semver, continuing from package.json's 0.1.0. Bump guide —
 *   • patch (0.0.x)  → small         fixes, copy, minor polish
 *   • minor (0.x.0)  → medium / big  new features, reworks
 *   • major (x.0.0)  → reserved for Stage 3 / breaking changes
 * The `impact` tag is independent of the bump and drives the coloured badge.
 *
 * To log a release: add a new object at the TOP of CHANGELOG with the next
 * version, today's date (YYYY-MM-DD), an impact level, a title, and bullet
 * `changes`. Newest first.
 */

// Impact levels → badge styling + ordering weight.
export const IMPACT = {
  small:    { label: 'Small',    weight: 1, bg: '#EEF1F5', fg: '#5B6472', border: '#D4DAE2' },
  medium:   { label: 'Medium',   weight: 2, bg: '#E8F1FE', fg: '#1A56C4', border: '#B6D5FB' },
  big:      { label: 'Big',      weight: 3, bg: '#FDF1DF', fg: '#9A5A12', border: '#F4D8AC' },
  critical: { label: 'Critical', weight: 4, bg: '#FCEBEC', fg: '#A4262C', border: '#F3C6C9' },
  // Brand-teal milestone badge — reserved for go-live / launch markers.
  golive:   { label: 'Live',     weight: 5, bg: '#E0F5F2', fg: '#0D9488', border: '#A7DED6' },
};

export const CHANGELOG = [
  {
    version: '0.56.3',
    date: '2026-10-22',
    impact: 'small',
    title: 'Add user from any view; Temporary clinic access gets its own tab',
    changes: [
      '"+ Add user" is now available in All users, Direct access and Groups, not only under Direct access.',
      '"Give access" on a directory match opens the add form where you are instead of switching to Direct access.',
      'Temporary clinic access moves out of the Show list to its own tab, between Users & access and Access log (SuperAdmin). Its email box suggests people from the Microsoft 365 directory as you type.',
    ],
  },
  {
    version: '0.56.2',
    date: '2026-10-22',
    impact: 'small',
    title: 'Users & access: compact filter row',
    changes: [
      'The search box, Show and Sign-in now sit on one line. Show and Sign-in are dropdowns instead of two stacked rows of buttons, and Sign-in lists how many people each option holds.',
    ],
  },
  {
    version: '0.56.1',
    date: '2026-10-22',
    impact: 'small',
    title: 'Users & access: search by name, clearer filters',
    changes: [
      'Search in Users & access matches names as well as emails, word by word in any order, so "eve sy" finds Eve Sydenham. All users shows each person\'s name next to their email.',
      'The second row of filters is now labelled Sign-in (Any / Signed in / Never signed in) and shows how many people each one holds for the current search.',
      'The Microsoft 365 directory section under the list only shows people who do not have AE Tracker access yet, each with a Give access button. Anyone with access is in the list itself.',
    ],
  },
  {
    version: '0.56.0',
    date: '2026-10-22',
    impact: 'medium',
    title: 'Users & access lists everyone with access, linked to the Microsoft 365 directory',
    changes: [
      '"Active users" is now "All users" and lists everyone with access: people who have signed in, clinic mailboxes, and every member of a role group (e.g. AE-Track-ClinicStaff) or direct assignment who has not signed in yet, shown as "Never signed in · no PIN". New Everyone / Signed in / Never signed in filter.',
      'Searching Users & access also searches the live Microsoft 365 directory by name or email, and shows matching people who are not in the list with the access they have (or "No AE Tracker access" and a Give access button).',
      '"+ Add user" and the Roles "+ Add" box suggest people from the directory as you type, so a new starter can be added as soon as IT creates their account. Each suggestion shows the access they already have.',
      'The access list now reads every page of role assignments from Entra. It used to stop at the first 200, so anyone past that would silently have been missing (140 today, so nobody was affected yet).',
    ],
  },
  {
    version: '0.55.2',
    date: '2026-10-21',
    impact: 'small',
    title: 'Tidier incident-specific details',
    changes: [
      '"Training date for the treatment concerned" is now just "Training date".',
      'The incident-specific details labels have a little more room, and a long label now stops at two lines (hover to read it in full), so the values sit higher and the panel takes less space.',
    ],
  },
  {
    version: '0.55.1',
    date: '2026-10-13',
    impact: 'small',
    title: 'Clinic PIN session timeout no longer loses a submission',
    changes: [
      'When a clinic login\'s PIN session times out (60 idle minutes), the PIN pad now appears over the page instead of the submission failing with "Your role cannot create cases in this scope". Everything typed stays, and a refused submit goes through as soon as the PIN is entered.',
      'The session is checked when a page opens, when the tab comes back into view and once a minute. These checks do not count as activity, but typing and clicking do, so the timeout now means 60 minutes of real inactivity.',
      '"Not you? Switch user" on the PIN pad lets a different person take over the terminal (their own sign-in, a fresh page).',
    ],
  },
  {
    version: '0.55.0',
    date: '2026-10-12',
    impact: 'medium',
    title: 'Notification escalation steps and Except recipients',
    changes: [
      'Scheduled rules can escalate: add steps such as "from 8 days, send to the head of the team" to the same rule instead of making a second rule. Each case only gets its highest step, and escalation stops once the conditions no longer match.',
      'Any recipient row can be set to Except, which removes those people from everyone else on the list, e.g. a role except its head.',
      'The rule preview shows how many of today\'s cases sit at each escalation step.',
    ],
  },
  {
    version: '0.54.0',
    date: '2026-10-12',
    impact: 'medium',
    title: 'Notification recipients: searchable roles and job titles',
    changes: [
      'The "Role / job title" recipient is now a searchable list of every job title currently held in the staff directory and every AE Tracker role, with how many people hold each.',
      'Picking an AE role notifies everyone who holds that role, including through a group.',
      'Recipients are worked out when the email is sent, so people who join or leave a role are picked up automatically.',
    ],
  },
  {
    version: '0.53.3',
    date: '2026-10-12',
    impact: 'small',
    title: 'Admin Panel waits for the configuration before showing editors',
    changes: [
      'Refreshing the Admin Panel on the Notifications tab no longer shows an empty rule and group list.',
      'Saving recipient groups from a freshly loaded page no longer replaces the existing groups.',
    ],
  },
  {
    version: '0.53.2',
    date: '2026-10-08',
    impact: 'small',
    title: 'Fixes to the stopped-publish flow',
    changes: [
      'Roles: if a publish is stopped, roles you had deleted in your draft no longer come back.',
      'The “Save failed” banner now clears when you click Load latest.',
    ],
  },
  {
    version: '0.53.1',
    date: '2026-10-08',
    impact: 'medium',
    title: 'Publishing settings can no longer wipe out someone else’s newer change',
    changes: [
      'If someone else has published a settings change since you opened the page, your publish is now stopped instead of quietly overwriting theirs. On the Roles tab, a Load latest button brings their changes in and keeps your unpublished edits.',
      'Roles tab: roles that exist in Entra but have not been set up in AE Tracker now appear under “In Entra — not set up”. People holding one cannot sign in until it is set up, and clicking it starts the setup.',
      'Users & Security: the role picker when adding a user now matches the app’s other dropdowns.',
    ],
  },
  {
    version: '0.53.0',
    date: '2026-10-08',
    impact: 'medium',
    title: 'Incident Category column, a cleaner test-clinic view, and adding people from the Roles tab',
    changes: [
      'All Cases has a new optional Incident Category column — switch it on from the column chooser. Long categories are shortened to 30 characters (hover to see the full text), and when the category is “Other” you see what was typed, in italics.',
      'You can also sort by Incident Category and find cases by typing a category into the search box.',
      'Super Admins: the Test clinic chip now shows only the cases filed against a test clinic, as its own list (like Deleted). Before, it mixed them in with every other case.',
      'Super Admins: “Who holds this role” on the Roles tab has a + Add button. Someone who already has a different role is moved to this one (you’ll be asked first) rather than given two roles.',
    ],
  },
  {
    version: '0.52.0',
    date: '2026-09-30',
    impact: 'medium',
    title: 'Set how long before your session auto-locks',
    changes: [
      'Supervisors and Super Admins can now choose their own auto-lock time from the user menu (next to Lock now and Change PIN): 5, 15, 30 or 60 minutes. Super Admins also get 2 hours, 4 hours, and Never.',
      'Your choice is saved to your account, so it follows you to any browser or device. Leave it alone and nothing changes — you stay on your role’s usual timing.',
      'Super Admins have never auto-locked; picking a time now turns auto-lock on for them, and “Never” keeps it off. After choosing a time, the lock screen will greet you on your next reload — that’s expected.',
    ],
  },
  {
    version: '0.51.1',
    date: '2026-09-24',
    impact: 'medium',
    title: 'Fix: assuming a clinic mailbox asked for a PIN, then trapped you there',
    changes: [
      'A clinic signs in as one shared mailbox, so assuming that identity put the clinic\u2019s own sign-in screen in front of you and asked for a staff member\u2019s personal PIN \u2014 which nobody troubleshooting could know, and which would have been refused anyway (nothing can be saved while assuming). You now simply pick whose view to look through.',
      'That sign-in screen covers the whole app, including the purple banner, so there was no way back out except signing out of your own account. The picker now carries its own Exit, and once you have chosen someone the banner returns \u2014 naming the person, with Change person beside Exit.',
      'The person you pick is checked against that clinic\u2019s staff list on every request, and you see exactly what they see: the cases they reported, nothing else at the clinic.',
    ],
  },
  {
    version: '0.51.0',
    date: '2026-09-23',
    impact: 'medium',
    title: 'The access log names the person behind a clinic mailbox, and sorts',
    changes: [
      'A clinic signs in as one shared mailbox that any front-of-house member uses, so a sign-in row could only ever say “wimbledon@therapieclinic.com”. The log now records the staff member who signed in, shown above the mailbox \u2014 the clinic and the person, not one or the other.',
      'That is written when the staff member enters their PIN, which is the first moment the app knows who they are: the browser reaches the app before anyone picks a name. So a clinic shows the mailbox arriving, then each person signing in behind it.',
      'Every column in the Access log now sorts \u2014 when, user, role, event, IP and country. Sorting reorders the whole log rather than the page you are looking at, and blank values stay at the bottom either way. A third click returns to newest-first.',
    ],
  },
  {
    version: '0.50.2',
    date: '2026-09-23',
    impact: 'medium',
    title: 'Fix: the test clinic\u2019s mailbox could not sign in',
    changes: [
      'When Thérapie Maps moved test clinics onto their own list, AE stopped being able to look one up at all \u2014 so the test clinic\u2019s mailbox could not sign in, its Clinic staff list reported the account was not a recognised clinic mailbox, and its existing cases had nothing to resolve their region against.',
      'AE now takes test clinics from that list for lookups while still keeping them out of every clinic picker except a SuperAdmin\u2019s. Both of the routes AE can read clinic data through now behave the same way, so the behaviour no longer depends on which one answered.',
    ],
  },
  {
    version: '0.50.0',
    date: '2026-09-21',
    impact: 'medium',
    title: 'Test clinics are separated from real ones',
    changes: [
      'Thérapie Maps now keeps test clinics in a separate list from the real ones. AE follows that split: a test clinic is offered only to SuperAdmins, is labelled Test wherever it appears, and nobody else sees it in a clinic picker at all.',
      'Cases filed against a test clinic are marked as such when they are created, and are left out of every number the app reports — the dashboard, per-clinic counts, the Monitor rollups and exports, the daily digest and the SLA figures. The mark is stored on the case, so a test case stays identifiable even after the clinic is unmarked in Maps.',
      'Those cases are visible to SuperAdmins only, behind a “Test clinic” chip in All Cases that is off by default, so the list and the dashboard agree unless you ask to see them.',
      'Notification rules and SLA escalations still run on a test case — that is how you test them — but the emails go only to whoever is testing. A real Head of Region or Ops Director is never told about a test case, and the case History records what was withheld and who it would have gone to.',
      'The seven cases already filed against the test clinic were marked in the same change, so historic totals line up with the new ones.',
    ],
  },
  {
    version: '0.49.3',
    date: '2026-09-21',
    impact: 'small',
    title: 'The muted flag is now a hollow flag',
    changes: [
      'A sign-in whose alerts are muted now shows a hollow grey flag rather than a crossed-out one \u2014 the same shape as the red ticket flag, so the two read as one signal with two states. Drawn with the icon set the rest of the app already uses, so nothing new was added to load.',
    ],
  },
  {
    version: '0.49.2',
    date: '2026-09-21',
    impact: 'small',
    title: 'A flag instead of a badge on flagged sign-ins',
    changes: [
      'In the access log, a sign-in that raised an IT ticket now carries a red flag in front of the country code, and a muted one a crossed-out flag \u2014 instead of a badge after the code, which pushed the country codes out of line.',
    ],
  },
  {
    version: '0.49.1',
    date: '2026-09-21',
    impact: 'small',
    title: 'Sortable user columns, and who holds a role shown beside the role',
    changes: [
      'Users & access columns sort — click a header to sort the whole list, again to reverse, a third time to go back to the natural order. Empty values always sort last, so “never signed in” does not fill the top of a descending sort. Sorting by Status puts the clinics with people waiting for approval first.',
      'The Roles tab now lists who holds the selected role beside its definition, with a dropdown to move someone to another role without leaving the page. Roles held through an Entra group are noted separately, since group membership is managed in Entra.',
    ],
  },
  {
    version: '0.49.0',
    date: '2026-09-21',
    impact: 'medium',
    title: 'Users & Security rebuilt — full width, and sign-ins show what security did about them',
    changes: [
      'Users & Security is now the first tab in the Admin Panel, and the tabs are ordered by how often they are used: Users & Security, Notifications, Roles, Tags & Rules, Cost, Incident Model, Case Config, Developer, Changelog.',
      'Users & access and the Access log are now two sub-tabs across the full width of the window, instead of two half-width panels where every email, role and action wrapped onto a second line.',
      'In Sign-ins, a sign-in from an unexpected country now shows what happened to it: red for one that raised an IT ticket, grey for one a mute deliberately silenced. Clicking either opens Security alerts with the person and country already filled in, so pausing their alerts is a duration and a reason.',
      'The header reads simply “N users · N direct” — four competing totals for the same thing was more confusing than helpful.',
    ],
  },
  {
    version: '0.48.3',
    date: '2026-09-21',
    impact: 'medium',
    title: 'Out-of-country sign-in alerts now repeat daily, and look like the rest of AE Tracker',
    changes: [
      'The alert IT receives when someone signs in from outside the expected countries now repeats once a day while it continues, instead of once per trip. It counted sign-ins rather than alerts, so an eleven-day stay abroad raised a single ticket on day one and nothing after.',
      'That email is now built from the standard AE Tracker template \u2014 branded header, labelled rows and footer \u2014 rather than the plain unstyled HTML it had been sending.',
    ],
  },
  {
    version: '0.48.2',
    date: '2026-09-21',
    impact: 'medium',
    title: 'Every clinic is now in Active users, with what it has waiting',
    changes: [
      'Clinics are listed in Users & Security ▸ Active users at all times. A shared clinic mailbox has no personal PIN, and that list was built from PIN holders \u2014 so a clinic only appeared once one of its staff had set one, and a clinic with an access request waiting was invisible exactly when someone needed to act on it.',
      'Each clinic row shows what it has outstanding \u2014 “1 waiting · 3 approved · 1 declined” \u2014 with anything waiting picked out, and Edit staff opens the full list to approve, decline or remove.',
    ],
  },
  {
    version: '0.48.1',
    date: '2026-09-21',
    impact: 'small',
    title: 'Response SLA figures in the Ops Monitor',
    changes: [
      'The Ops Monitor (Super Admin) now reports on the Code 4/5 response clock alongside the existing case and notification figures.',
    ],
  },
  {
    version: '0.48.0',
    date: '2026-09-21',
    impact: 'medium',
    title: 'Security alert follow-up, clinic notification history, and a truer user count',
    changes: [
      'Sign-ins from outside the expected countries still alert IT \u2014 and now every alert is recorded in the access log, sent or failed, so “was IT told?” is answerable instead of assumed.',
      'Once IT has verified someone\u2019s travel, a Super Admin can mute their alerts for 7, 30 or 90 days \u2014 for one country or any country \u2014 from Users & Security ▸ Access log ▸ Security alerts. Muting only stops the email: the sign-in is still logged, and every mute expires.',
      'Clinic access requests and approvals now record who was emailed about them, so an approval sitting untouched can be traced to whether anyone was actually told.',
      'Users & access shows how many people are really onboarded: everyone with direct access, each clinic mailbox counted once, plus each clinic\u2019s named staff from the second person onward.',
      'Users & access is easier to read \u2014 email addresses sit on one line, the role is fully visible, and Remove is a bin icon.',
    ],
  },
  {
    version: '0.47.1',
    date: '2026-09-19',
    impact: 'small',
    title: 'The clinic manager follows the clinic you pick',
    changes: [
      'Changing the clinic on a new submission now always updates Clinic manager responsible — including clearing it when that clinic has no manager on record. Previously it only changed when the new clinic had one, so a manager from a previously selected clinic could be filed against the case.',
    ],
  },
  {
    version: '0.47.0',
    date: '2026-09-17',
    impact: 'medium',
    title: 'Saved views, refusal register, and a tidier case page',
    changes: [
      'All Cases: save the filters, columns and sort you use as a named view — star it by the title, pick your views from the chevron, and mark one as your default. Views are private to you.',
      'A column chooser beside Deleted picks what the table shows (including the new Last activity and SLA columns), and the choice is remembered on this device. There are also Selected / All / Deleted views and an Inactive-for filter for finding stale cases.',
      'Countries, clinics and tags are now picked from a dialog and shown as pills you can remove one at a time. Tags are three-state: has, off, or doesn\u2019t have — so you can exclude as well as include.',
      'Privacy mode in the user menu frosts photo thumbnails on case files for when someone can see your screen; open a photo to view it in full.',
      'A tidier case page: severity and status are controls in the header, the response clock and the legal deadline share one row, and incident details sit in two columns.',
      'Comments record which side wrote them, so clinic-side and head-office messages are distinguishable in the history.',
      'The developer role-preview control has been removed — role scoping has been enforced server-side since v0.39.1 and the preview no longer matched what the server returns.',
      'Near miss reports filed under Compliance can now be logged as a Refusal Register entry — the record of a booking or procedure that was refused, for example when someone could not provide acceptable ID under the Challenge 25 policy.',
      'Choosing that category swaps the form for the five things a refusals register has to carry: the date and time of the request, why it was refused, a brief description of the potential buyer, the action taken, and the staff member who refused it. No corrective action, assignee or target date — the refusal was the action.',
      'Submitting one notifies people as usual, tags it RefusalRegister and closes it automatically. The tag is applied by the system and cannot be removed, so nothing can quietly drop out of the register.',
      'To pull a register for an audit, filter All Cases by the RefusalRegister tag — picking it also switches the Closed filter on, since every entry is closed. You still only see your own clinic\u2019s cases.',
      'The case file, its edit pop-up and the printed case report all show the register fields, and there is a new Knowledge article, "Recording a refusal (refusal register)".',
    ],
  },
  {
    version: '0.46.0',
    date: '2026-09-09',
    impact: 'big',
    title: 'Required evidence, mandatory fields, document reminders and a Code 4/5 response clock',
    changes: [
      'The new-case form now blocks Continue and Submit until every field marked with an asterisk is filled in — previously the markers were advisory and an incomplete report could still be submitted. The form highlights what’s missing and jumps to it.',
      'Client phone number, staff member concerned (enter N/A if nobody was involved), incident category and — for LHR and AWI — the severity code are now required on treatment adverse events, and the server enforces them too.',
      'Consent form on every treatment submission: upload it, or switch to “No consent form” and record why. The declaration shows on the case file; only Supervisors can add or withdraw it there, and uploading the form later withdraws it automatically.',
      'Code 3, 4 and 5 cases record a Review date at submission (“Once the review has taken place, please update the case file on AE Track by adding a comment”). It appears in Case information and can be edited there.',
      'Post photos ask for the date the photo was taken before the upload starts; training certificates ask for the training date and trainer name. Both show under the file on the case file and in comments.',
      'The training certificate (and its training date / trainer) is no longer requested at submission — it stays on the case file for the training team to fulfil.',
      'AWI cases gain optional Product used, Area treated, Dosage and Symptoms fields on every code, and “Injector notified of adverse event and legal action” is now an optional Yes / No switch on all AWI codes.',
      'Notification rules can now target cases with an outstanding required document or field (optionally older than N days), send one email per case so CC recipients land on the same message, and address the Country manager for the case’s country — the building blocks for the daily document reminders and the 7-day escalation to the Head of RO and Country manager.',
      'Code 4 and Code 5 cases carry a response clock: HQ (Head of RO / Country manager / Supervisor) has six working hours to respond, then the clinic has six, alternating until the case is closed. The clock pauses while a case is Waiting on Client or after the message is acknowledged; breaches email the Head of RO and Country manager; Supervisors can lift a hold or suppress the clock. Off by default until enabled in Admin ▸ Case Config.',
      'Every message now has an Acknowledge action — a light-touch “seen” that is recorded in the case history without sending a notification.',
      'Groundwork for shared clinic mailboxes (per-person sign-in with a PIN, approvals inbox, case sharing) ships in this release but stays switched off until the clinic roll-out.',
    ],
  },
  {
    version: '0.45.8',
    date: '2026-09-04',
    impact: 'medium',
    title: 'Assume identity now requires a real user',
    changes: [
      'Assume identity no longer asks you to pick a role — enter the user\'s email and their actual role and clinic/region scope are looked up from the directory.',
      'Impersonating an email that doesn\'t belong to an existing user with an AE Tracker role is now rejected (previously an invented email plus an unrestricted role could show every case).',
      'The server re-verifies the impersonated user and role on every request, so a stale or tampered session can\'t view as someone who doesn\'t exist.',
    ],
  },
  {
    version: '0.45.7',
    date: '2026-09-03',
    impact: 'small',
    title: 'Status changes use a folders icon in case history',
    changes: [
      'Status change entries on the case history timeline now show a folders icon in place of the previous marker.',
    ],
  },
  {
    version: '0.45.6',
    date: '2026-09-03',
    impact: 'small',
    title: 'Tag events use a tag icon in case history',
    changes: [
      'Adding or removing a tag now shows a tag icon on the case history timeline instead of the generic three-dot marker — a tag removal is marked in red, to match how other removals appear.',
    ],
  },
  {
    version: '0.45.5',
    date: '2026-09-03',
    impact: 'small',
    title: 'Clearer icon for notifications in case history',
    changes: [
      'Notification entries in the case history now use a distinct notification icon (in indigo) instead of the generic three-dot marker, so they are easier to pick out on the timeline.',
    ],
  },
  {
    version: '0.45.4',
    date: '2026-09-03',
    impact: 'medium',
    title: 'Case history now shows notifications again',
    changes: [
      'Fixed: the case history was silently leaving out every "Notified: …" entry, so you could not see that a notification had been sent or who received it — even though the emails were going out correctly.',
      'All notification activity now appears on the timeline again, each with its recipients (collapsed past two lines), including the "Notification sent to: …" line folded under a comment when someone is tagged.',
      'This was a database-query fault, not missing data — every past notification reappears immediately, nothing was lost.',
    ],
  },
  {
    version: '0.45.3',
    date: '2026-09-02',
    impact: 'small',
    title: 'Case history shows who was emailed, without clicking',
    changes: [
      'When a notification goes out, the case history now shows the recipient email addresses right under the event, instead of hiding them behind a "Show recipients" click.',
      'Long recipient lists stay tidy: anything beyond two lines is collapsed behind a "Show all" toggle.',
    ],
  },
  {
    version: '0.45.2',
    date: '2026-09-01',
    impact: 'medium',
    title: 'Heads of RO can hold a second region, and pending clinics are hidden',
    changes: [
      "A Head of RO covering a second region (set in Thérapie Maps as a secondary region) now sees that region's cases too, exactly like their own. This backs the new London re-organisation, where one HoRO also oversees GB South and East.",
      "Notifications routed to the case's regional Head of RO now reach the secondary HoRO alongside the primary. Where a region has no primary HoRO at all, the secondary receives them alone.",
      'Clinics marked Pending in Therapie Maps (not yet open) no longer appear in the clinic pickers on New Submission or the case file. They appear automatically once the clinic opens. Closed clinics remain selectable for historical cases, as before.',
    ],
  },
  {
    version: '0.45.1',
    date: '2026-08-29',
    impact: 'small',
    title: 'Health endpoints locked down',
    changes: [
      'The public health check at /api/health now only reports that the service is up. It no longer runs database, Graph or storage checks, and no longer includes internal names or error details in a response anyone on the internet can read.',
      'The detailed health breakdown (database, Graph, blob storage) moved behind the SuperAdmin gate at /api/monitor/health. The Ops Monitor health pills read from there, and a failing dependency now shows as a red pill instead of a blank one.',
      'The individual probes at /api/health/db, /api/health/graph and /api/health/blob also require SuperAdmin now. External uptime monitoring is unaffected: it checks /api/health, which still answers 200.',
    ],
  },
  {
    version: '0.45.0',
    date: '2026-08-14',
    impact: 'medium',
    title: 'Open case counts now appear on the clinic card in Thérapie Maps',
    changes: [
      'Thérapie Maps can now show a clinic’s open AE cases on its clinic card — how many are open, how many are With Claims, how long the oldest has been open, and the split by status and by treatment. Clicking any number opens that clinic’s cases here, already filtered.',
      'Only counts leave AE Tracker. No client name, phone, staff member, case reference or note is shared — the feed is aggregate figures and nothing else, and a test enforces that so a future field cannot slip in.',
      'Who can see it is decided in Maps, not here: it is off until a Maps admin grants the “AE cases card” permission to a role, and only for clinics that person can already see. Nothing in AE Tracker changes for anyone using it.',
      'Nothing about how you work changes. The case list, filters and links are exactly as they were — the filter rules are now shared with the new feed so a count on the Maps card and the list it opens can never disagree.',
    ],
  },
  {
    version: '0.44.1',
    date: '2026-08-13',
    impact: 'small',
    title: 'Clinic, Region and Country are picked from the clinic list, not typed',
    changes: [
      'Editing a case file’s Clinic, Region or Country now uses searchable dropdowns from the clinic directory instead of free-text boxes. A typo there used to drop the case out of its clinic’s stats, the region filters and Head of RO routing, with nothing to flag it.',
      'Choosing a clinic fills in its Region and Country automatically, the same way New Case does. Both stay editable — but only to values that exist in the directory, and anything that disagrees with the chosen clinic is called out with a one-click fix.',
      'A clinic that is no longer in the directory (renamed or closed) is kept as it is rather than silently cleared.',
    ],
  },
  {
    version: '0.44.0',
    date: '2026-07-31',
    impact: 'medium',
    title: 'Comment notifications reach everyone tagged, and PIN auto-lock is now per-role',
    changes: [
      'Fixed: tagging a Head of RO (or anyone) on a comment could send no email at all. Staff addresses from the directory are now cleaned before use, one bad address no longer blocks the whole notification, and a failed send is shown instead of a silent success.',
      'The recipient picker now flags anyone whose address can’t be notified, and the case history shows “Notification sent to: …” under the comment so you can see exactly who was emailed.',
      'PIN session-lock timings are now set per role in Admin ▸ Roles: how long until auto-lock, how long a hidden tab waits before locking, and an “exempt from auto-lock” switch. Left blank, a role inherits the defaults (15 min idle / 30 s hidden tab).',
    ],
  },
  {
    version: '0.43.1',
    date: '2026-07-21',
    impact: 'small',
    title: 'With Claims replaces Waiting on Client in the case filters',
    changes: [
      'The All Cases quick-filter card and status checkbox for Waiting on Client (unused, 0 cases) are replaced by With Claims, with a live count \u2014 on the Dashboard KPI row and status chart too.',
      'With Claims cases no longer leak into every filtered list: a case only appears under its own status filter. Statuses without their own filter chip stay out of any narrowed view.',
    ],
  },
  {
    version: '0.43.0',
    date: '2026-07-21',
    impact: 'big',
    title: 'Dashboard, Knowledge and Reports are now separate role permissions',
    changes: [
      'The single "Reports" role permission split into three independent toggles in Admin \u25b8 Roles: Dashboard (the analytics page), Knowledge (the in-app user guide), and Reports (the case-file Report tab \u2014 printable case report plus attachment/ZIP downloads).',
      'Defaults: Dashboard and Knowledge are on for every role; Reports is Admin tier and up (Supervisor, Supervisor (view), Admin, Super Admin) \u2014 Head of RO, Clinic Manager and Read Only no longer see the case Report tab unless granted.',
      'Fixes the missing HoRO dashboard: role rows saved before a permission existed silently disabled it. A saved role definition now only changes what it explicitly sets \u2014 anything unset follows the code default for that role.',
      'Custom roles that had the old Reports permission keep their Dashboard access automatically.',
    ],
  },
  {
    version: '0.42.3',
    date: '2026-07-20',
    impact: 'small',
    title: 'Case file: Attachments shows every file',
    changes: [
      'The Attachments panel on the case file now lists every file attached to the case, however it was added — required-document slots, per-case requirements, comments, or the submission wizard. Previously non-photo files uploaded into a required slot appeared only inside that slot.',
      'Required documents & sign-offs still shows each slot\'s files in place, so slot files now appear in both sections.',
    ],
  },
  {
    version: '0.42.2',
    date: '2026-07-20',
    impact: 'small',
    title: 'Ops Monitor: simpler timeline presets (30d / YTD / All time)',
    changes: [
      'Reworked the timeline presets to 30d, YTD (year to date), All time, and Custom — dropped 7d / 90d / 1y.',
      '"All time" removes the date bound entirely, so the counts and charts cover everything on record.',
    ],
  },
  {
    version: '0.42.1',
    date: '2026-07-20',
    impact: 'small',
    title: 'Admin Panel opens on Users & Security + access-log tidy-up',
    changes: [
      'The Admin Panel now lands on the Users & Security tab when opened (deep links from other pages still go to their requested tab, and admins without that tab land on their first available one).',
      'Access log: an admin PIN reset no longer names the same admin twice — the row reads "By <admin>" once instead of "By <admin> · Reset by <admin>".',
    ],
  },
  {
    version: '0.42.0',
    date: '2026-07-20',
    impact: 'medium',
    title: 'Ops Monitor: custom date range + hide SP-migrated cases',
    changes: [
      'New "Custom" option on the timeline lets you pick an exact From/To date range, alongside the 7d/30d/90d/1y presets. It drives every range-based view — Cases, Notifications, Users, API metrics, and the exported reports.',
      'The Overview "Cases" card now counts cases created within the selected range (previously an all-time snapshot), so it tracks the timeline like the other views.',
      'New "Hide SP-migrated" toggle drops cases imported from SharePoint out of the case counts (Overview card + the Volumes report), so the numbers reflect organic activity only. Off by default.',
    ],
  },
  {
    version: '0.41.20',
    date: '2026-07-20',
    impact: 'small',
    title: 'Access log: Country column as a globe icon',
    changes: [
      'The "Country" header is now a small globe icon (hover for the label), reclaiming the width the word took — the User column gets it, so long emails stop wrapping.',
    ],
  },
  {
    version: '0.41.19',
    date: '2026-07-17',
    impact: 'medium',
    title: 'New cases always start at "New"',
    changes: [
      'A newly submitted case now always starts at "New", whoever creates it. Previously a case logged by an Admin or Supervisor was born "Pending", which made it look worked when nobody had touched it.',
      'It still moves to "Pending" automatically on the first genuine activity by someone other than the person who reported it (a comment, edit, requirement, file, cost or tag).',
    ],
  },
  {
    version: '0.41.18',
    date: '2026-07-17',
    impact: 'medium',
    title: 'Requirement rules now fire at case creation',
    changes: [
      'Matrix-driven requirements (the documents a case needs from its treatment + severity, e.g. Training certificate on LHR Code 3) now trigger "Requirement added" notification rules the moment the case is submitted — previously only hand-added requirements did.',
      'The rule preview counts cases by the requirements they actually carry (matrix-driven or added) instead of always showing 0 for requirement rules, and shows which requirement matched per case.',
    ],
  },
  {
    version: '0.41.17',
    date: '2026-07-17',
    impact: 'small',
    title: 'Access log: column polish',
    changes: [
      'IP and Country swapped — Country is now the last column, right-aligned.',
      'Timestamps drop the seconds (When and Last unlock), freeing space for the user column.',
    ],
  },
  {
    version: '0.41.16',
    date: '2026-07-17',
    impact: 'small',
    title: 'Animated halo on the sign-in screen',
    changes: [
      'The login card has a slowly rotating, morphing teal glow (ported from Therapie Maps). Honours reduced-motion settings.',
      'Smoothed the faint banding rings in the login page background.',
    ],
  },
  {
    version: '0.41.15',
    date: '2026-07-17',
    impact: 'small',
    title: 'Access log: cleaner IP and timestamp alignment',
    changes: [
      'IPs right-align to the column edge (the per-octet spacing looked gappy).',
      'Timestamps keep one column but split visually: date pinned left, time pinned right.',
    ],
  },
  {
    version: '0.41.14',
    date: '2026-07-17',
    impact: 'small',
    title: 'Access log: Country and IP columns',
    changes: [
      'Detail is split into Country (hover for the full name) and IP columns.',
      'IPs, country codes and timestamps use a monospace font; IPv4 octets are aligned so the dots line up down the column.',
    ],
  },
  {
    version: '0.41.13',
    date: '2026-07-17',
    impact: 'medium',
    title: 'Access log: country + IP, and IT alerts for unexpected countries',
    changes: [
      'The Detail column now shows the country code before the IP (e.g. "IE · 84.203.…"), resolved locally — no staff IPs leave the platform.',
      'A sign-in from outside GB / IE / US / PH / CZ / PT emails a security alert to IT support (at most once per user and country in 24 hours).',
    ],
  },
  {
    version: '0.41.12',
    date: '2026-07-17',
    impact: 'small',
    title: 'Access log: IP address in the Detail column',
    changes: [
      'Sign-ins, PIN unlocks and PIN set/change events now record the IP address they came from — shown in the Detail column (older rows stay blank).',
    ],
  },
  {
    version: '0.41.11',
    date: '2026-07-17',
    impact: 'small',
    title: 'Aligned table rows + full-height monitor chart',
    changes: [
      'Users & access and Access log rows are now the same height (42px), so the two cards line up row-for-row.',
      'Ops Monitor: the Notifications · last 24h chart stretches to fill its card — no dead space below; hover a bar for its exact count.',
    ],
  },
  {
    version: '0.41.10',
    date: '2026-07-17',
    impact: 'small',
    title: 'Ops Monitor: readable notifications chart',
    changes: [
      'Overview cards swapped: Notification rules now sits next to Cases, with the Notifications · last 24h chart below.',
      'The 24h chart has time labels on its axis (auto-spaced, not every hour), so you can see when notification volume peaks.',
    ],
  },
  {
    version: '0.41.9',
    date: '2026-07-16',
    impact: 'small',
    title: 'Users & access: one-line rows with an inherited-group icon',
    changes: [
      'The "inherited through AE-Track-…" text under a user\'s role is now a small group icon — hover it to see the group. Rows are one line, so the table shows 18 users per page (level with the Access log card).',
      'Columns spread across the full card width (wider User and Role columns).',
      'Fixed the "115 userss" / "323 sign-inss" double-plural typos.',
    ],
  },
  {
    version: '0.41.8',
    date: '2026-07-16',
    impact: 'small',
    title: 'Case Config: page size + sort share a card',
    changes: [
      'Default Page Size and Default Sort now live side by side in one card — both are tiny All Cases preferences.',
    ],
  },
  {
    version: '0.41.7',
    date: '2026-07-16',
    impact: 'small',
    title: 'Case Config: packed columns, no dead space',
    changes: [
      'Case Config panels now stack in two balanced columns — Status Values sits directly under Event Types, Treatment Types under Severity Codes — instead of row-aligned cards leaving gaps under the short ones.',
    ],
  },
  {
    version: '0.41.6',
    date: '2026-07-16',
    impact: 'small',
    title: 'Case Config layout fixed at capped widths',
    changes: [
      'Case Config panels no longer squeeze into clipped columns — every section (severity, status, treatments, …) gets a full readable column and they pair up when there is room.',
      'Tags & Rules header buttons no longer wrap into the description at capped width.',
      'Access log trimmed to 18 rows per page — 20 ran slightly taller than the Users & access card.',
    ],
  },
  {
    version: '0.41.5',
    date: '2026-07-16',
    impact: 'small',
    title: 'Admin panel: consistent widths everywhere',
    changes: [
      'Every admin tab now uses the same readable max width (Case Config, Incident Model, Tags & Rules, Cost and Developer join the tabs capped in v0.41.4).',
      'Access log shows 20 rows per page so its card levels up with Users & access when they share a row.',
    ],
  },
  {
    version: '0.41.4',
    date: '2026-07-16',
    impact: 'small',
    title: 'Admin panel: readable widths on big screens',
    changes: [
      'Users & Security, Notifications, Roles and Changelog panels now cap at a readable width instead of stretching across the whole monitor; two panels share a row when there is room (e.g. Users & access next to Access log).',
      'Removed the reference-only SuperAdmin Emails list and the Reset-to-defaults danger zone — the live access gate is Entra, and a blanket config reset was riskier than useful.',
    ],
  },
  {
    version: '0.41.3',
    date: '2026-07-16',
    impact: 'small',
    title: 'Temporary clinic access folded into Users & access',
    changes: [
      'Temporary clinic access is now a view inside Users & access — ordered Active users / Direct access / Temporary clinic access / Groups — instead of its own panel.',
      'The grant form fits one row on wide screens (user email · expires · clinics), wrapping on narrow ones.',
      'The email filter box also narrows the grants list.',
    ],
  },
  {
    version: '0.41.2',
    date: '2026-07-16',
    impact: 'small',
    title: 'Users & Security: tighter layout',
    changes: [
      'Users & access and Temporary clinic access now sit side by side on wide screens instead of each taking a full row (stacked again on narrow windows).',
      'Staff email fields: placeholder now reads "username".',
    ],
  },
  {
    version: '0.41.1',
    date: '2026-07-16',
    impact: 'small',
    title: 'Staff email fields: type the username, pick the domain',
    changes: [
      'Temporary clinic access, Assume identity and Add user no longer ask for a full email address — type just the username and pick the domain from a dropdown (defaults to therapieclinic.com). No more domain typos.',
      'Pasting a full address still works: the username and domain split out automatically.',
      'therapieclinic.ph added to the recognised staff domains alongside .com and .us.',
    ],
  },
  {
    version: '0.41.0',
    date: '2026-07-15',
    impact: 'medium',
    title: 'Custom statuses now work end-to-end + per-treatment availability',
    changes: [
      'Fixed: a status added in the Admin Panel (e.g. "With Claims") appeared in the dropdown but the server refused to save it — case writes now accept any published status alongside the built-ins.',
      'Status values can now be limited per treatment type (LHR, AWI, …), one level below the existing event-type availability. None ticked = all treatments; the case-file status menu follows the case\'s treatment, and legacy alias-stored treatments (e.g. "LASER") resolve correctly.',
      'Restricted statuses show their treatment list as a pill in the Admin Panel row.',
    ],
  },
  {
    version: '0.40.0',
    date: '2026-07-15',
    impact: 'medium',
    title: 'Region access now follows the live TherapieMaps mapping',
    changes: [
      'Clinics get re-assigned between regions in TherapieMaps periodically. Regional access (HoRO and region-scoped custom roles) now resolves the region\'s CURRENT clinic list on every request — a re-mapping takes effect immediately, instead of cases staying with the region title stamped at submission.',
      'HoRO notifications route by the clinic\'s current region too, so a re-mapped clinic\'s cases alert the right Head of RO.',
      'A nightly job (02:30) re-stamps the region stored on each case to the current mapping, keeping dashboards, reports and rule conditions consistent; runs are visible in Ops Monitor.',
    ],
  },
  {
    version: '0.39.2',
    date: '2026-07-15',
    impact: 'small',
    title: 'Friendlier subject line on batched notification emails',
    changes: [
      'Batched ("coalesced") notification emails now use a readable subject — e.g. "AE-1814 · 2 updates · Therapie Clinic Clapham Junction" — instead of exposing the internal rule name.',
      'Update counts now pluralise naturally in subjects and email bodies (1 update / 2 updates).',
    ],
  },
  {
    version: '0.39.1',
    date: '2026-07-14',
    impact: 'small',
    title: 'Granted clinics now visible in the case list and dashboard',
    changes: [
      'A Clinic Manager with temporary access to another clinic could submit cases there but not SEE them — the case list and dashboard silently hid the granted clinic\'s cases. They now show everything the server grants access to.',
      'Under the hood: the app no longer re-filters real sessions client-side (the server already scopes every response, grants included); that filter now only runs for the local dev role preview it was built for.',
    ],
  },
  {
    version: '0.39.0',
    date: '2026-07-14',
    impact: 'medium',
    title: 'Triage is now Pending — and it sets itself automatically',
    changes: [
      'The "Triage" case status has been renamed to "Pending" everywhere — case list, dashboard, filters and reports. Existing Triage cases were moved over automatically.',
      'A case now flips from New to Pending on its own the first time someone on the handling side (a role that can edit cases or add requirements) works it — even just adding a comment. Manual status changes always win: if the first action sets a status, that status stands.',
      'Follow-up activity by the person who reported the case (for example "forgot to mention…") does NOT count — the case stays New until the handling side actually picks it up.',
      'Cases opened by the handling side themselves skip New and start at Pending directly.',
      'The automatic flip is recorded in the case history (marked as automatic) but deliberately sends no notifications.',
    ],
  },
  {
    version: '0.38.2',
    date: '2026-07-13',
    impact: 'small',
    title: 'Granted clinics selectable when filing a case',
    changes: [
      'A Clinic Manager with temporary access to another clinic can now choose it in the New Submission form (previously the clinic was locked to their home clinic).',
    ],
  },
  {
    version: '0.38.1',
    date: '2026-07-13',
    impact: 'small',
    title: 'Impersonation reflects clinic grants; monitor table fills the page',
    changes: [
      'Assume identity now shows a user\'s temporary clinic grants (within your own access), so you can verify a grant by viewing as that user.',
      'The Ops Monitor notifications table now fills the available space instead of scrolling inside a small box.',
    ],
  },
  {
    version: '0.38.0',
    date: '2026-07-13',
    impact: 'medium',
    title: 'Temporary clinic access',
    changes: [
      'SuperAdmins can now grant a user temporary access to one or more additional clinics on top of their normal access — without changing their role. Set it up in Admin ▸ Users & Security.',
      'Every grant needs an expiry date (up to 90 days) and revokes itself automatically once that date passes — no clean-up step needed. You can also revoke a grant early at any time.',
      'Grants only ever widen access, never restrict it: the person keeps everything their role already gives them, plus the added clinic(s) until the grant expires.',
    ],
  },
  {
    version: '0.37.1',
    date: '2026-07-13',
    impact: 'small',
    title: 'Batched notifications: quiet-period + max cap',
    changes: [
      'Rules with "batch rapid updates" turned on now wait for a short quiet period after the LAST update before sending the digest — instead of a fixed window timed from the first update. So a burst of activity is gathered together and the summary goes out once things settle down.',
      'A maximum cap was added so a case that keeps getting updated can never hold its digest forever — once the cap is reached (30 minutes by default) the summary is sent even if the case is still active. Both the quiet period and the cap are adjustable per rule.',
    ],
  },
  {
    version: '0.37.0',
    date: '2026-07-13',
    impact: 'medium',
    title: 'Batch rapid updates + scheduled digests in the monitor',
    changes: [
      'Notification rules can now "batch rapid updates" — turn it on for a rule and a flurry of events on one case (e.g. six files uploaded in a minute) is held briefly and sent as a single digest email instead of one per event. Off by default; urgent rules stay instant.',
      'Scheduled daily-report digests (e.g. HoRO daily) now appear in the Ops Monitor → Notifications feed as a per-send summary ("… digest → N recipients · M cases"), so you can see they went out. They no longer clutter individual case timelines.',
    ],
  },
  {
    version: '0.36.1',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor — fix Notifications tab failing to load',
    changes: [
      'The Notifications tab was erroring with a SQL syntax message; the filter-options query used a reserved keyword as a column alias. Now fixed — the tab loads and filters again.',
    ],
  },
  {
    version: '0.36.0',
    date: '2026-07-10',
    impact: 'medium',
    title: 'Incident description is now a permanent, read-only record',
    changes: [
      'The incident description entered at submission is now shown as its own read-only card on the case and can no longer be edited by anyone — it’s a permanent record of the original report, so a case can’t be tampered with after it’s raised.',
      'Medical notes are now a separate field, added after submission only by admins/supervisors. Previously the submitted description and any later notes shared one editable field.',
      'Applies to new submissions across all event types (adverse events, near-miss, clinic incidents). Existing cases are unchanged — their original text stays in the notes field.',
    ],
  },
  {
    version: '0.35.8',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor — paginated Notifications + chart date labels',
    changes: [
      'The Notifications feed is now paged 20 at a time and loads only the page you’re viewing — search and the Rule / Event / Status filters run on the server, and the feed follows the timeline range at the top of the page (like the other views), so the live dashboard stays light no matter how many notifications have been sent.',
      'The New users and Sign-ins bar charts now label the dates under the bars, thinning them as the panel narrows and falling back to a start–end range caption when there’s no room for individual ticks.',
    ],
  },
  {
    version: '0.35.7',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor — Notifications table + filters',
    changes: [
      'The Notifications feed is now an inline table with When / Case / Rule / Event / Status / Recipients columns and a sticky header, so it’s easier to scan who was notified and when.',
      'Added filters for troubleshooting — a search box (matches recipient email, case, rule or time) plus Rule / Event / Status dropdowns, with a live filtered count and Clear. Failed sends are tinted red.',
    ],
  },
  {
    version: '0.35.6',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor — full-width layout',
    changes: [
      'The Ops Monitor now fills the content area like every other page. Previously it was capped at 1200px, which in dark mode left a light gutter down the right-hand side.',
    ],
  },
  {
    version: '0.35.5',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor — Users panel: chart numbers + list filters',
    changes: [
      'The New users and Sign-ins charts now show the count above each bar and a period total in the heading.',
      'Added filters to the Users list — search by email plus Role / Region / Source dropdowns and a “Locked only” toggle, with a live filtered count and a Clear button.',
    ],
  },
  {
    version: '0.35.4',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor — align numeric table columns',
    changes: [
      'Right-aligned the numeric column headers (Requests / Failed / p95 / Duration / Days left) in the Ops Monitor tables so they line up with their values instead of sitting to the left.',
    ],
  },
  {
    version: '0.35.3',
    date: '2026-07-10',
    impact: 'small',
    title: 'Incident category editable everywhere; treatment locked after submission',
    changes: [
      'The Incident category is now genuinely editable for every treatment when editing a case incident — including treatments with no subtypes (e.g. LHR), where the field was still hidden before.',
      'Treatment / Department is now read-only on the edit form. Changing it would switch the whole data structure (subtype/category cascade and type-specific fields) and orphan captured data — if the treatment is wrong, re-submit the case. Subtype stays editable.',
    ],
  },
  {
    version: '0.35.2',
    date: '2026-07-10',
    impact: 'small',
    title: 'Incident category always editable on the case file',
    changes: [
      'When editing a case’s treatment incident, the Incident category is now always editable — including for treatments/severities that have no preset category list (it falls back to “Other” free text). Previously the field was hidden in those cases.',
    ],
  },
  {
    version: '0.35.1',
    date: '2026-07-10',
    impact: 'small',
    title: 'Ops Monitor contrast fixes',
    changes: [
      'Recipient email chips now pick a legible text colour by the chip’s actual brightness (no more dark-on-blue), and dark-mode headings/tables use the correct dark-theme colours.',
    ],
  },
  {
    version: '0.35.0',
    date: '2026-07-10',
    impact: 'big',
    title: 'Ops Monitor for SuperAdmins',
    changes: [
      'A new SuperAdmin-only Ops Monitor (in your user menu) shows platform health, notification activity, scheduled jobs, user adoption & usage, secret expiry, and API traffic — all read-only, no client data.',
      'Downloadable Excel reports — user adoption, usage, case volumes, and API calls — over an adjustable timeline.',
      'Adjustable time window across the views and reports, plus a dark-mode toggle.',
    ],
  },
  {
    version: '0.34.2',
    date: '2026-07-09',
    impact: 'small',
    title: 'No more slow first load after idle',
    changes: [
      'The app now keeps itself warm in the background, so the first page load after a quiet spell (e.g. first thing in the morning) is no longer slow — it stays responsive instead of “waking up”.',
    ],
  },
  {
    version: '0.34.1',
    date: '2026-07-09',
    impact: 'medium',
    title: 'Faster Users & access loading',
    changes: [
      'The Admin ▸ Users & Security list opens much faster: user directory details are fetched in a single batch instead of one lookup per person, and the group-membership checks for active users now run in parallel instead of one-at-a-time — which was the main cause of the long wait (several seconds down from ~10s+).',
    ],
  },
  {
    version: '0.34.0',
    date: '2026-07-09',
    impact: 'big',
    title: 'US clinics (therapieclinic.us) supported end-to-end',
    changes: [
      'Adverse events can now be logged for US clinics: the new-case country toggle offers US alongside GB and Ireland, and the case list has a matching US country filter.',
      'Roles can be scoped to the US the same way as GB and Ireland, so US operations staff see only their own cases.',
      'Costs on US cases default to US Dollars (UK cases → GBP, Ireland → EUR) so spend is recorded in the right currency automatically; you can still override the currency per cost.',
      'Staff on @therapieclinic.us addresses can now receive notifications and scheduled digests.',
    ],
  },
  {
    version: '0.33.0',
    date: '2026-07-09',
    impact: 'small',
    title: 'Developer API reference replaces the legacy SharePoint tab',
    changes: [
      'The Admin panel’s old “System / Legacy” tab — SharePoint-era list checks and identifiers, unused since data moved to SQL — has been removed.',
      'A new “Developer” tab takes its place: a read-only API reference (base URL, sign-in model, and the full endpoint catalogue), visible to SuperAdmins.',
      'Retired the unused “Administrator (view)” role, which had never been set up as an assignable role.',
    ],
  },
  {
    version: '0.32.0',
    date: '2026-07-07',
    impact: 'big',
    title: 'Requirement permissions split; Clinic Manager & HoRO become responders',
    changes: [
      'Requirement handling is now two separate permissions — "Add requirements" (create the ask; Admin/Supervisor) and "Respond to requirements" (upload the requested file or complete a sign-off) — so teams such as Service can respond to a request without any broader edit rights.',
      'Clinic Manager and HoRO can no longer edit case fields once a case is submitted: they submit, comment, resolve, and respond to requirements. Editing fields, escalating, adding requirements and deleting stay with Admin/Supervisor — responders can only add, never alter (a wrong file is fixed by uploading the correct one and noting it in a message).',
      'The role editor gains independent Add / Respond toggles, ready for composing new team roles.',
    ],
  },
  {
    version: '0.31.0',
    date: '2026-07-07',
    impact: 'medium',
    title: 'Submission details fully mirrored on the case file',
    changes: [
      'Every treatment/code-specific detail captured at submission (laser serial number, laser settings, client skin type, training date, injector acknowledgement, …) now appears on the case in a dedicated "Incident-specific details" section, with an "N outstanding" count so missing items are obvious.',
      'Admins can fill any of these in later from Edit Incident Details — including fields a Clinic Manager was never asked for at submission time.',
      'Inputs now show a live character counter wherever a limit applies, and long text wraps and grows to fit instead of clipping.',
    ],
  },
  {
    version: '0.30.2',
    date: '2026-07-07',
    impact: 'golive',
    title: 'AE Tracker fully live',
    changes: [
      'The platform is fully live in production: case intake and lifecycle, the notification rules engine with scheduled digests, roles and scoped access, costs, tags, and reporting are all deployed and in daily use.',
      'From here on, releases are incremental improvements on a live system.',
    ],
  },
  {
    version: '0.30.1',
    date: '2026-07-07',
    impact: 'medium',
    title: 'Requirement-type conditions for notification rules',
    changes: [
      'Rules on Requirement added / Sign-off completed can now condition on WHICH requirement it is — Requirement type (from the admin-managed list) and Requirement label — so HR, Training, or Service can each be routed their own document requests (e.g. laser details → Service).',
      'Sign-off requirements carry a dedicated "signoff" type on both add and completion, so one rule covers the full sign-off flow.',
    ],
  },
  {
    version: '0.30.0',
    date: '2026-07-07',
    impact: 'big',
    title: 'Notification rules v2',
    changes: [
      'New rule wizard (Admin ▸ Notifications ▸ Add rule) with live preview: matching cases and the actual resolved recipients, before you save.',
      'Richer conditions: OPS (IOI/GB), severity-level comparisons (e.g. at least Code 4), previous status/severity on change triggers, and per-slot file counts.',
      'New triggers: case updated, file uploaded, requirement added, comment added (public), and private comment added (deliberate opt-in, existence-only email).',
      'One rule can watch multiple triggers; each rule sends its own email with an optional subject suffix and note. The person who performed the action is never notified about it.',
      'Recipient groups: named staff lists (e.g. Training team) managed on the Notifications tab, usable in any rule.',
      'Scheduled rules: a daily 08:00 UTC scan for milestone and inactivity alerts plus daily-report digests — each region/clinic recipient only sees their own cases. Ships OFF until enabled in config.',
      'Every rule-driven send is recorded on the case timeline (addresses visible to SuperAdmins only).',
      'Fixed: rule preview showed the total case count instead of matches; rule emails were missing the Open case link.',
    ],
  },
  {
    version: '0.29.16',
    date: '2026-07-06',
    impact: 'medium',
    title: 'Case submit failure ops alerts',
    changes: [
      'Failed case submissions email ops with reporter, SQL errors, field-length analysis, and the full payload — no need to ask users for screenshots.',
      'Users see a clearer message when submit fails; generic transaction-abort text is replaced with support-notified wording.',
    ],
  },
  {
    version: '0.29.15',
    date: '2026-07-06',
    impact: 'small',
    title: 'Resolve separate from status & code',
    changes: [
      'HoRO and Clinic Manager can Mark resolved (close with a reason) without Change status and code — severity and workflow status edits stay Admin+.',
      'Resolve and Reopen are independent toggles in Roles admin; no longer stripped when status & code is off.',
    ],
  },
  {
    version: '0.29.14',
    date: '2026-07-06',
    impact: 'small',
    title: 'Group-inherited roles on Active users',
    changes: [
      'Users & access ▸ Active users now resolves Entra group roles per PIN user (checkMemberGroups) when bulk group enumeration is unavailable — clinic staff from AE-Track-ClinicStaff show their inherited role.',
    ],
  },
  {
    version: '0.29.13',
    date: '2026-07-06',
    impact: 'medium',
    title: 'Roles overhaul, scoped impersonation, case capabilities',
    changes: [
      'Roles admin reworked: case capabilities split into Submit / Edit / Change status & code; Reports access toggle; priority-based impersonation hierarchy; HoRO and CM scopes locked to TherapieMaps assignment.',
      'Assume identity is now region-safe — a HoRO (or anyone viewing as a scoped user) cannot switch to a Clinic Manager outside their assigned region; switch-identity probes the API before applying and shows a clear error.',
      'Impersonation scope anchor: when switching identity while already viewing as someone, case lists, stats, and reference data stay ceilinged to that person\'s assignment — not the signed-in SuperAdmin\'s full access.',
      'HoRO and Clinic Manager scopes always resolve from TherapieMaps by email, even if a governed role definition still says scope "all".',
      'Reference clinics API no longer returns the full staff directory when the caller is scoped — only in-assignment clinics and contacts.',
      'Request requirements moved under Cases and requires Post comments; the Requirements menu is hidden when comments are off.',
    ],
  },
  {
    version: '0.29.12',
    date: '2026-07-06',
    impact: 'medium',
    title: 'Client phone, AWI subtype, CM clinic prefill, wizard labels',
    changes: [
      'Client phone number now appears on the treatment case detail page and printed report (it was captured at submission but not shown).',
      'AWI (injectables) subtype is now a dropdown with an Other free-text option in New Submission and case edit — custom text is stored directly, not the word "Other".',
      'Clinic Managers with a single assigned site get their clinic pre-filled and read-only again; the GB/IOI country toggle only appears for admins or CMs assigned to clinics in both Ireland and the UK.',
      'New Submission step 1 is labelled General info instead of "Common header", with Knowledge Base copy updated to match.',
    ],
  },
  {
    version: '0.29.11',
    date: '2026-07-04',
    impact: 'medium',
    title: 'Therapist statement can be typed in',
    changes: [
      'The Treating therapist statement slot now opens a text pop-up — type the statement and it saves to the case as a text document (previews like any attachment). Attaching a signed or scanned file is still available from the same pop-up.',
      'Works both when filing a case and on the case file afterwards.',
    ],
  },
  {
    version: '0.29.10',
    date: '2026-07-04',
    impact: 'medium',
    title: 'New Submission — cleaner document slots + CM-shaped requirements',
    changes: [
      'The repetitive "Needed for completion — click to upload" on every document slot is gone — the instruction now appears once under the Documents heading, and long document names show in full on hover.',
      'Clinic Managers filing a case are no longer asked for HQ-held items (training date, trainer name, training certificate, laser log files) — those still appear on the case file for the responsible teams to fulfil.',
    ],
  },
  {
    version: '0.29.9',
    date: '2026-07-04',
    impact: 'medium',
    title: 'Assume identity grantable to custom roles',
    changes: [
      'The Roles tab has a new "Assume identity" capability, so custom roles (e.g. the Directors of Operations) can be granted the troubleshooting view-as tool.',
      'Guardrails hold regardless of who impersonates: only lesser roles can be assumed, nothing can be saved while assuming, and costs/private comments never become visible to someone whose own role hides them.',
    ],
  },
  {
    version: '0.29.8',
    date: '2026-07-04',
    impact: 'big',
    title: 'Assume identity — full view parity',
    changes: [
      'Assuming an identity now shows the app exactly as that user sees it — comment composer, New Submission wizard, edit controls and menus all render and open, instead of being hidden by a read-only view.',
      'Nothing can be saved or posted while assuming: posting a comment or submitting a case shows a clear notice, and the server continues to reject every change attempted during an assumed session.',
      'Assumable roles are now capped at lesser roles, in the picker and at the API: Super Admin can never be assumed, and Supervisor only by a real Super Admin (covered by tests).',
      'Banner and Knowledge guidance updated to match ("actions disabled" instead of "read-only").',
    ],
  },
  {
    version: '0.29.7',
    date: '2026-07-04',
    impact: 'small',
    title: 'Downloads save to disk + tidier attachment cards',
    changes: [
      'Every Download action (preview toolbar, report archive, requirement file rows) now saves the file to disk instead of opening it in a new browser tab.',
      'Attachment cards drop their per-card Download button — click previews the file (Download is in the preview toolbar), and non-previewable files download directly on click.',
    ],
  },
  {
    version: '0.29.6',
    date: '2026-07-04',
    impact: 'medium',
    title: 'Attachment previews restored + preview polish',
    changes: [
      'Fixed previews and downloads being dead for every attachment — a v0.29.4 API regression stopped download URLs being generated (now covered by a regression test).',
      'Clicking the dark area around a previewed file now closes the preview.',
      'Files in Required documents & sign-offs open the same preview overlay as the Attachments panel, instead of a new browser tab.',
    ],
  },
  {
    version: '0.29.5',
    date: '2026-07-04',
    impact: 'medium',
    title: 'Multi-file uploads + photos in Attachments',
    changes: [
      'Every upload picker (wizard slots, case-detail required slots, per-case requirements, comment attachments) now accepts multiple files in one go.',
      'Photos uploaded against a required slot now also appear in the Attachments panel with image previews, instead of only as filenames in the slot.',
      'The required upload is now labelled Photos (was Photo) — it almost always holds more than one file. Existing files keep working unchanged.',
    ],
  },
  {
    version: '0.29.4.1',
    date: '2026-07-03',
    impact: 'critical',
    title: 'Hotfix — tag without a label blanked All Cases',
    changes: [
      'A tag saved without a label crashed the All Cases page (tag sorting and display assumed every tag has one). Tags now fall back to showing their slug, with the same guard on empty clinic names, and the crash is covered by tests.',
      'The hotfix was briefly lost the same evening when a feature deploy overwrote it — re-applied together with boot-stage diagnostics, so a future load failure reports which step failed instead of a blank page.',
    ],
  },
  {
    version: '0.29.4',
    date: '2026-07-03',
    impact: 'big',
    title: 'Security hardening + HoRO case creation fix',
    changes: [
      'Notifications can only be sent by roles that can comment, and only to internal staff addresses — external recipients are rejected and comment text is no longer included in the email.',
      'View-only roles (AdminView, SupervisorView) can no longer create cases via the API.',
      'Resetting another user’s PIN now always requires a real Super Admin — it can no longer be done while using Assume identity.',
      'Clinic Managers no longer receive private-comment attachments or snippets through the case or timeline views.',
      'Assume identity no longer exposes Super Admin-only screens (access management, config audit) to Supervisors.',
      'Heads of Regional Operations can now file a case — the clinic field is a region-scoped picker instead of an empty read-only field, and the region is enforced on save.',
    ],
  },
  {
    version: '0.29.3',
    date: '2026-07-02',
    impact: 'small',
    title: 'All Cases — Quick filters label',
    changes: [
      'The All Cases header toggle is now labelled Quick filters (was Stats), matching what the status KPI row actually does.',
      'Knowledge article and demo copy updated to match.',
    ],
  },
  {
    version: '0.29.2',
    date: '2026-07-02',
    impact: 'small',
    title: 'Knowledge — All Cases stat shortcuts demo',
    changes: [
      'The Finding & viewing ▸ All Cases article now includes an animated demo of the status stat cards filtering the case table.',
      'Replaces the search-box demo on that article so the guide matches how users actually narrow the list from the KPI row.',
    ],
  },
  {
    version: '0.29.1',
    date: '2026-07-02',
    impact: 'small',
    title: 'LHR Code 2 — full list for Admin+',
    changes: [
      'LHR Code 2 keeps the full clinical category list (Leucotrichia, paradoxical hypertrichosis, Fox Fordyce, etc.) for Admin-tier roles when submitting and in the case file.',
      'Reporters still see the shorter Code 2 list at submission (Folliculitis and Other); the reference guide matches that reporter-facing set.',
    ],
  },
  {
    version: '0.29.0',
    date: '2026-07-02',
    impact: 'medium',
    title: 'Tag and impersonation role gates',
    changes: [
      'Assume identity is now available to Supervisors and Super Admins (read-only preview of any user’s scope).',
      'Creating new tags (case picker or Admin Panel) is Supervisor/Super Admin only; assigning existing tags on cases is Admin and above — HoRO and Clinic Manager can no longer add or remove tags.',
      'Knowledge articles and the in-app changelog reflect the split between tag creation and tag assignment.',
    ],
  },
  {
    version: '0.28.0',
    date: '2026-07-02',
    impact: 'medium',
    title: 'LHR submission polish',
    changes: [
      'LHR incident categories trimmed for Code 1 (PFO removed) and Code 2 (dermatologist-only diagnoses removed) — the dropdown, reference guide and vocab now stay in sync even when older admin config still lists retired options.',
      'The LHR code-reference rail matches the form height on wide screens and scrolls inside; selecting a code moves that card to the top so Code 5 is immediately visible.',
      'Required extra fields (training date, incident date/time) now use the themed calendar picker instead of native date inputs.',
    ],
  },
  {
    version: '0.27.0',
    date: '2026-07-02',
    impact: 'medium',
    title: 'Supervisor scoped admin panel',
    changes: [
      'Supervisors now get a focused Admin Panel — Tags & Rules, Cost categories/currencies, and the Changelog — without the full Super Admin surface.',
      'The API enforces the same scope: tag management endpoints accept Supervisor; config writes are limited to costCategories and currencies; creating a brand-new tag from a case is Supervisor/Super Admin only.',
      'Local dev: a role-preview picker in the user menu (and ?devRole=Supervisor URL) lets you preview any role’s UI without changing your Entra assignment.',
    ],
  },
  {
    version: '0.25.0',
    date: '2026-07-02',
    impact: 'medium',
    title: 'Themed calendar date picker',
    changes: [
      'Every date field in the app (new-case form, case editing, dashboard and case-list filters, costs) now opens a proper calendar when you click anywhere on the field — no more clicking the tiny icon or accidentally selecting the “dd” text.',
      'The calendar matches the app’s look: Monday-first grid, today highlighted, out-of-range days greyed out, and Today / Clear shortcuts. Fully keyboard-navigable (arrows, PageUp/Down, Enter).',
      'Fixed a bug where “today” couldn’t be selected between midnight and 1am Irish time — date limits were being computed in UTC.',
    ],
  },
  {
    version: '0.24.0',
    date: '2026-07-01',
    impact: 'big',
    title: 'Manage user access in-app · welcome emails',
    changes: [
      'Admin ▸ Users & Security is now a single place to manage everyone’s access — see each person’s role, change it, add a new user (email + role), or remove access. Changes are written to Entra (their app sign-in role) and take effect on next sign-in.',
      'Adding a user emails them a confirmation — what the tool is, their role, and a link to sign in.',
      'People & Access and Security are merged into one “Users & Security” tab, and each user row now also carries its PIN reset (with last-unlock time), paginated 10 at a time.',
    ],
  },
  {
    version: '0.23.0',
    date: '2026-07-01',
    impact: 'medium',
    title: 'Codes only where they apply · editable Near Miss & Clinic Incident',
    changes: [
      'Severity codes now appear only for LHR and AWI — the treatments that actually use them. For every other treatment the severity picker is hidden when logging a case, and no severity is shown on the case file or report (the incident category is still captured).',
      'Near Miss and Clinic Incident cases are now fully editable: their details panel has an Edit button to correct the reporting function, cause, corrective action, assignee, injured-person and witness details, category and dates — previously these were view-only.',
      'A required Photo still applies to every treatment except AWI, including the ones that don’t use codes.',
    ],
  },
  {
    version: '0.22.0',
    date: '2026-07-01',
    impact: 'medium',
    title: 'New event type — Clinic Incident',
    changes: [
      'The new-case form now supports “Clinic incident” — a non-treatment incident at a clinic (a staff accident at work, or a member of the public injured on the premises).',
      'Capture the type of incident, who was hurt, what happened, the nature of any injuries and the immediate response — plus optional witness details. Investigation and corrective actions are handled on the case afterwards, like every other type.',
      'Clinic incidents show as their own “Clinic Incident Report” in the case view and printed report.',
    ],
  },
  {
    version: '0.21.0',
    date: '2026-07-01',
    impact: 'big',
    title: 'More treatments, tag colours & editing, richer case edit',
    changes: [
      'The new-case form now covers more treatments — Skin Peels, Skin Treatment, Potenza / Morpheus 8, Hydrafacial, Micro-needling, Tattoo Removal, CoolSculpt, Exilis, Emsculpt and Clear + Brilliant — with “Other” always last in the list.',
      'Every treatment except AWI now asks for a Photo. Documents a case needs appear under “Required for this code” — they’re what the case needs to be complete, not to submit it, so anything you can’t provide now can be added later.',
      '“Not AE”: admins can reclassify a case as not an adverse event during review (it’s never offered at submission) so these can be tracked separately in the dashboards.',
      'Editing a case now covers the full detail set — treatment subtype, incident category and client phone, alongside the existing fields.',
      'Tags: pick any colour — the palette or a custom colour — and mark a tag as shown in the picker or search-only. You can now rename and recolour existing tags too. Creating a brand-new tag from a case is limited to Supervisors and Admins.',
    ],
  },
  {
    version: '0.20.0',
    date: '2026-06-30',
    impact: 'big',
    title: 'Notifications — custom flow rules & visibility',
    changes: [
      'New Admin ▸ Notifications page: see and control every email the system sends about a case, in one place.',
      'Create custom rules — when a case event matches your conditions (treatment, severity, region, clinic, status…), notify the right people by email, job title, role preset (e.g. Injectables lead), Head of RO (by region) or Clinic Manager (by clinic).',
      'Choose To / Cc / Bcc for each recipient.',
      'The built-in notifications are now listed for visibility — and the new-case recipients and the treatment-based routing (e.g. AWI → Injectables lead) are editable instead of buried in code.',
      'The condition builder now offers dropdowns of real values and a live “how many cases match” preview (this also improves Tags & Rules).',
      'Notifications carry only the case reference, clinic and a link — never client names or clinical detail, and only ever to staff addresses.',
    ],
  },
  {
    version: '0.19.0',
    date: '2026-06-29',
    impact: 'medium',
    title: 'Comments composer & type-aware report',
    changes: [
      'The comment box is tidier: a single “+ Add” menu opens focused pop-ups for an Attachment, a Notification, or a Requirement, and the Public/Private switch has moved up beside the formatting tools (with an “i” that explains the difference).',
      'Requesting a document or sign-off now happens when you post a comment — it adds the requirement to “Required documents & sign-offs”, records a “Requested …” note in the thread, and (if you choose recipients) emails them that they have an action on the case.',
      'The Report tab adapts to the case type: the header colour and title differ for Adverse Event (teal), Near miss (blue) and Clinic incident (amber), and the fields shown match the type.',
      'Report polish — the preview now stays in view as you scroll the section list, the layout stacks neatly on a phone, and the page uses more of its space.',
      'The “Required documents & sign-offs” and “Attachments” cards now appear only once they have something to show, and the comment formatting tools collapse into a compact menu on small screens.',
    ],
  },
  {
    version: '0.18.0',
    date: '2026-06-20',
    impact: 'medium',
    title: 'Admin panel redesign',
    changes: [
      'The admin panel is reorganised into clearer tabs (Case Config, Incident Model, Tags & Rules, Cost, People & Access, Security, System/Legacy, Changelog) so settings are easier to find — and the tab bar is keyboard-navigable.',
      'Better use of space: settings sit side-by-side on a responsive grid, and the incident-category lists are laid out in columns instead of one long stack.',
      'Built to scale: incident categories collapse into accordions, and long Tags / Session-PIN lists now have search + paged navigation.',
      'Legacy SharePoint settings are clearly marked as reference-only, and the SuperAdmin-emails list is labelled “reference only — the live gate is Entra”.',
    ],
  },
  {
    version: '0.17.0',
    date: '2026-06-19',
    impact: 'big',
    title: 'Assume identity (read-only)',
    changes: [
      'SuperAdmins can now view the app exactly as another user — pick a role and enter their email to reproduce what they see when troubleshooting.',
      'Strictly read-only while assumed: no edits, comments, status changes or deletions; a persistent banner shows whose view you’re in, with an always-available Exit that can never lock you out.',
      'Your own PIN still applies while assuming an identity; replaces the old shared automation test account.',
    ],
  },
  {
    version: '0.16.0',
    date: '2026-06-19',
    impact: 'big',
    title: 'Auto-tag rules',
    changes: [
      'Tags can now be applied automatically by rules: in Admin → Case Types → Tags, build an AND/OR condition (e.g. treatment = AWI AND country = UK) and a locked tag like #UK-AWI is applied to every matching case.',
      'Rules are live: a tag is added when a case matches and removed when it no longer does (on create, edit, and the SharePoint import).',
      'The rule builder shows a live “matches N cases” count so you can see the impact before saving, and “Re-apply rules” backfills existing cases.',
      'Rule/auto tags are locked — they can’t be added or removed by hand, so the classification stays trustworthy.',
    ],
  },
  {
    version: '0.15.0',
    date: '2026-06-19',
    impact: 'big',
    title: 'Case tags',
    changes: [
      'Cases can now be tagged: a "+ Add tag" picker sits under the Mark-resolved / Delete buttons — autocomplete from existing tags, create a new one inline, remove with ×, all keyboard-navigable.',
      'Filter the case list by tag (from the full tag list, with counts).',
      'System tags like #sp_migrated — applied automatically to every case imported from SharePoint — are locked and can’t be removed by hand.',
      'Tag changes are permission-checked and recorded in the case history, the same as other case edits.',
    ],
  },
  {
    version: '0.14.0',
    date: '2026-06-19',
    impact: 'big',
    title: 'Incident categories: structured cascade editor',
    changes: [
      'Incident categories are now edited in a structured cascade (treatment → subtype → severity code) with per-item add / edit / remove / drag-reorder — replacing the free-text boxes.',
      'The cascade is config-driven end-to-end: treatments and subtypes come from your taxonomies, so the new-case form updates with no code change.',
      'Publishing writes the treatment subtypes and their category branches together in ONE transaction, so a failure can never leave them out of sync.',
      'Renaming a subtype moves its categories with it; removing one asks whether to keep or delete them — nothing is silently lost.',
      'Editing no longer dead-ends when a treatment was deleted: leftover category branches are cleaned up on publish instead of blocking it.',
    ],
  },
  {
    version: '0.13.0',
    date: '2026-06-19',
    impact: 'big',
    title: 'Admin config: governance, audit trail & schema-driven editors',
    changes: [
      'Severity codes, statuses and treatment types now use one consistent editor with inline editing, drag-reorder and live validation.',
      'High-impact taxonomy changes go through a draft → review → publish step that previews the exact before/after diff before anything goes live.',
      'Every config change is recorded in a tamper-evident, append-only audit trail (who, when, before→after), with a one-click integrity check in the change history.',
      'Validation and permissions are now enforced on the server too — a bad or unauthorised change is rejected, not just hidden in the UI.',
    ],
  },
  {
    version: '0.12.0',
    date: '2026-06-18',
    impact: 'medium',
    title: 'Editable incident categories',
    changes: [
      'The new-case incident-category cascade (per treatment + severity code, incl. AWI Toxin/Filler) is now editable in Admin → Case Types → Incident Categories — no deploy needed to adjust the clinical lists.',
      'Categories are config-driven with a safe fallback to the previous built-in list; a parity test guards that the seed matches.',
    ],
  },
  {
    version: '0.11.0',
    date: '2026-06-18',
    impact: 'medium',
    title: 'Admin-editable event types',
    changes: [
      'Event types (Treatment / Near miss / Clinic incident) are now editable in Admin → Case Types: rename, change the hint, enable/disable, and reorder.',
      'The new-case wizard reads event types from config; a disabled type can’t be selected or submitted.',
      'The availability checkboxes across the vocabulary editors now reflect your configured event types and labels.',
    ],
  },
  {
    version: '0.10.0',
    date: '2026-06-18',
    impact: 'big',
    title: 'Event-type availability — config now drives the forms',
    changes: [
      'Treatment types, severity codes and statuses can be tagged (in Admin → Case Types) with which event types they apply to — empty means all types.',
      'The new-case wizard filters its treatment and severity pickers by the chosen event type, and resets a selection that no longer applies.',
      'Case detail status / severity / treatment dropdowns filter the same way (an existing value is always kept).',
      'Server-side guard rejects an unavailable treatment/severity for the event type (off by default until launch).',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-06-18',
    impact: 'big',
    title: 'Security hardening, automated tests & CI',
    changes: [
      'Notification emails now escape all user/case text (closes an HTML-injection path); comment notifications no longer carry the comment body.',
      'Attachment uploads enforce a 50 MB cap and a file-type allow-list — checked in the browser before sending and again on the server.',
      'First automated test suite (30+ tests) covering access-control scoping, email escaping, upload limits and form/config logic.',
      'GitHub Actions CI runs the tests and frontend build on every push; pinned Node 22 (API) / 24 (frontend).',
      'Severity display reverted to “Code 1–5”; fixed a comment-posting error when notifying recipients.',
    ],
  },
  {
    version: '0.8.0',
    date: '2026-06-18',
    impact: 'medium',
    title: 'Severity labels, cost sorting & admin-panel rework',
    changes: [
      'Added a “No code” severity filter so cases with no severity assigned can be filtered to or out.',
      'Case list is now sortable by Cost (sums all currency totals as the sort key).',
      'Admin Panel: new Cost tab with editors for Cost categories and Currencies — previously code-only.',
      'Admin Panel: new Changelog tab (this view), versioned with impact levels.',
    ],
  },
  {
    version: '0.7.0',
    date: '2026-06-18',
    impact: 'big',
    title: 'Rich-text comments & directory-driven notifications',
    changes: [
      'WYSIWYG comment editor (bold/italic/underline/strike, lists, link, brand colours); HTML sanitised with DOMPurify.',
      'Notification recipient picker rebuilt: grouped by department, multi-select, with “This case” populated from real HoRO / Clinic Manager names.',
      'Medical Team Member picker reads the live directory (HSRC + Injectables Growth), grouped by department, with manual fallback.',
      'New-case form reworked into two-column sections; escalation info text uses job titles, not names.',
    ],
  },
  {
    version: '0.6.0',
    date: '2026-06-18',
    impact: 'medium',
    title: 'Badge system & event-type filters',
    changes: [
      'Status badges recoloured on a blue→teal ramp; severity on a warm palette — the two systems now read distinctly.',
      'Chart colours are sourced from the badge config, so editing a code/status reaches the charts automatically.',
      'Case list Event filter: LHR / AWI / Near miss / Clinic incident / Other, each with live counts.',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-06-17',
    impact: 'medium',
    title: 'PIN for all roles, resolution card & search ranking',
    changes: [
      'Every role now requires a session PIN; SuperAdmin is exempt from auto-lock (manual lock only).',
      'Case view gained a resolution card (closed date + resolution) between Incident details and Staff.',
      'Report export with select/download-to-zip for attachments.',
      'Case-list search ranks exact case-ref hits first and falls back to close matches instead of returning nothing.',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-06-17',
    impact: 'critical',
    title: 'Server-side RBAC & data-source hardening',
    changes: [
      'Role-based access control enforced server-side in the API (no longer trusting the client).',
      'TherapieMaps clinic data served via the API; case clinics normalised to real TherapieMaps names.',
      'event_type wired end-to-end with SQL migration 007.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-06-16',
    impact: 'big',
    title: 'Shared-hardware PIN lock & notifications',
    changes: [
      'PIN session lock for shared-hardware roles (migration 005); /api/pin/* endpoints with back-off throttling.',
      '“New” status as the default for newly-submitted cases.',
      'Attachment UX extended into the case view and comments composer.',
      'Notification recipient resolution wired to TherapieMaps.',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-06-15',
    impact: 'medium',
    title: 'Cost chips & clinic picker',
    changes: [
      'Cost chip on case-list rows and history events.',
      'New-case GB/IOI clinic filter with a searchable clinic picker.',
      'API role-string checks migrated to capability helpers.',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-06-15',
    impact: 'big',
    title: 'Stage 2 foundation',
    changes: [
      'Login screen and expanded role model.',
      'Cost feature (categories, currencies, totals).',
      'Dashboard rollups and the case list.',
    ],
  },
];

/** Newest-first, defensively sorted by date then version. */
export function sortedChangelog() {
  return [...CHANGELOG].sort((a, b) =>
    a.date === b.date ? b.version.localeCompare(a.version, undefined, { numeric: true })
                      : (a.date < b.date ? 1 : -1),
  );
}
